import React, { useEffect, useState } from "react";
import { Dialog } from "primereact/dialog";
import { useDispatch, useSelector } from "react-redux";
import { getAmendHistoryList, resetAmendHistoryList } from "../../../../../../Store/prosafe/proTransaction/PurOrder/getAmendHistoryList";
import { getAmendHistoryData, resetAmendHistoryData } from "../../../../../../Store/prosafe/proTransaction/PurOrder/getAmendHistoryData";
import Loader from "../../../../Include/Loader";
import moment from "moment";

const fieldLabels = {
    party_id: "Supplier / Party",
    trans_date: "Order Date",
    freight_amount: "Freight Amount",
    payment_method_id: "Payment Method",
    quotation_id: "Quotation No",
    remarks: "Remarks",
    terms_condition: "Terms and Conditions",
    quantity: "Item Quantity",
    rate: "Item Rate",
    discount: "Discount (%)",
    gst: "GST (%)",
    total_amount: "Total Amount",
    taxable_amount: "Taxable Amount",
    gst_amount: "GST Amount",
    discount_amount: "Discount Amount",
    item_no: "Item Number",
    mrp: "MRP",
    item_weight: "Item Weight",
    amendment_reason: "Reason for Change",
    added_items: "Items Added",
    removed_item_ids: "Items Removed",
    modified_items: "Items Modified",
};

const getLabel = (field) => fieldLabels[field] || field.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());

const formatDisplayValue = (field, value) => {
    if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) return "-";

    // Format dates specifically
    if (field.includes("date") || field.includes("_at") || field === "createdAt") {
        const d = moment(value);
        if (d.isValid() && String(value).length > 5) {
            return d.format("DD-MM-YYYY");
        }
    }

    if (typeof value === "object") {
        if (Array.isArray(value)) {
            return `${value.length} Item(s)`;
        }

        if (value.name) return value.name;
        if (value._id) return "Record Updated";
        return "Modified";
    }

    if ((field.includes("amount") || field === "rate") && !isNaN(value)) {
        return Number(value).toLocaleString('en-IN');
    }

    return String(value);
};

const AmendHistoryModal = ({ visible, onHide, poId, voucherNo }) => {
    const dispatch = useDispatch();
    const [selectedAmend, setSelectedAmend] = useState(null);

    const { user: historyList, loading: listLoading } = useSelector((state) => state.getAmendHistoryList);
    const { user: historyData, loading: dataLoading } = useSelector((state) => state.getAmendHistoryData);

    useEffect(() => {
        if (visible && poId) {
            dispatch(getAmendHistoryList({ payload: { original_po_id: poId } }));
        }
        return () => {
            dispatch(resetAmendHistoryList());
            dispatch(resetAmendHistoryData());
            setSelectedAmend(null);
        };
    }, [visible, poId, dispatch]);

    const handleSelectAmend = (amend) => {
        setSelectedAmend(amend);
        dispatch(getAmendHistoryData({ payload: { amendment_id: amend._id } }));
    };

    const renderChangeLog = (changeLog) => {
        if (!changeLog || changeLog.length === 0) return (
            <div className="text-center py-5 text-muted">No specific field changes recorded.</div>
        );

        return (
            <div className="table-responsive mt-2">
                <table className="table table-sm" style={{ borderCollapse: 'separate', borderSpacing: '0 8px' }}>
                    <thead className="bg-light">
                        <tr style={{ fontSize: '11px' }}>
                            <th className="ps-3 border-0 py-2 text-uppercase text-muted">Field / Item</th>
                            <th className="text-center border-0 py-2 text-uppercase text-muted">Old Value</th>
                            <th className="text-center border-0 py-2"></th>
                            <th className="text-center border-0 py-2 pe-3 text-uppercase text-muted">New Value</th>
                            <th className="text-center border-0 py-2 pe-3 text-uppercase text-muted">Changed By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {changeLog.map((log, index) => (
                            <tr key={index} className="align-middle shadow-none" style={{ backgroundColor: '#fcfcfc' }}>
                                <td className="ps-3 py-2 border-top border-bottom">
                                    <div className="fw-bold text-dark" style={{ fontSize: '13px' }}>{getLabel(log.field)}</div>
                                    {log.item_id && <div className="text-muted small text-truncate" style={{ maxWidth: '250px', fontSize: '11px' }}>{log.item_id?.name}</div>}
                                </td>
                                <td className="text-center py-2 border-top border-bottom">
                                    <span className="text-danger fw-normal" style={{ fontSize: '13px' }}>
                                        {formatDisplayValue(log.field, log.old_value)}
                                    </span>
                                </td>
                                <td className="text-center py-2 border-top border-bottom">
                                    <i className="fa fa-arrow-right text-muted small" style={{ fontSize: '10px' }}></i>
                                </td>
                                <td className="text-center py-2 pe-3 border-top border-bottom">
                                    <span className="text-success fw-bold" style={{ fontSize: '13px' }}>
                                        {formatDisplayValue(log.field, log.new_value)}
                                    </span>
                                </td>
                                <td className="text-center py-2 pe-3 border-top border-bottom">
                                    <span className="fw-bold" style={{ fontSize: '13px', color: "var(--primary-clr)" }}>
                                        {formatDisplayValue("changed_by", log?.changed_by?.user_name)}
                                    </span>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        );
    };

    return (
        <Dialog
            header={
                <div className="d-flex align-items-center">
                    <div>
                        <span className="mb-0 fw-bold">Amendment History - {voucherNo}</span>
                    </div>
                </div>
            }
            visible={visible}
            onHide={onHide}
            style={{ width: '75vw' }}
            breakpoints={{ '960px': '85vw', '641px': '100vw' }}
            modal
            draggable={false}
            resizable={false}
            className="compact-history-dialog"
        >
            {(listLoading && !historyList?.data) ? (
                <div className="text-center py-5">
                    <Loader />
                </div>
            ) : (
                <div className="row g-0 border-top mt-2">

                    <div className="col-12 col-md-4 border-end bg-light">
                        <div className="list-group list-group-flush" style={{ maxHeight: '550px', overflowY: 'auto' }}>
                            {listLoading ? (
                                <div className="text-center py-5">
                                    <Loader />
                                </div>
                            ) : (
                                historyList?.data?.map((amend) => (
                                    <button
                                        key={amend._id}
                                        type="button"
                                        className={`list-group-item list-group-item-action border-bottom py-3 ${selectedAmend?._id === amend._id ? 'active' : ''}`}
                                        onClick={() => handleSelectAmend(amend)}
                                    >
                                        <div className="d-flex w-100 justify-content-between">
                                            <span className={`fw-bold ${selectedAmend?._id === amend._id ? 'text-white' : 'text-primary'}`}>
                                                {amend.amendment_no === 0 ? "Original PO" : `Amend #${amend.amendment_no}`}
                                                {amend.is_latest && (
                                                    <span className="ms-2 badge bg-success" style={{ fontSize: '10px' }}>
                                                        Current
                                                    </span>
                                                )}
                                            </span>
                                            <small className={selectedAmend?._id === amend._id ? 'text-white' : 'text-muted'}>
                                                {moment(amend.createdAt).format("DD/MM/YY")}
                                            </small>
                                        </div>
                                        <p className={`mb-1 mt-1 small text-truncate ${selectedAmend?._id === amend._id ? 'text-white' : 'text-muted'}`}>
                                            {amend.amendment_no === 0 ? "No Amendment data" : (amend.amendment_reason || "No reason.")}
                                        </p>
                                    </button>
                                ))
                            )}

                            {!listLoading && (!historyList?.data || historyList?.data?.length === 0) && (
                                <div className="text-center py-4 text-muted small">No history.</div>
                            )}
                        </div>
                    </div>

                    <div className="col-12 col-md-8 bg-white">
                        {!selectedAmend ? (
                            <div
                                className="text-center py-5 h-100 d-flex flex-column align-items-center justify-content-center"
                                style={{ minHeight: '400px' }}
                            >
                                <i className="fa fa-info-circle fa-2x text-light mb-2"></i>
                                <p className="text-muted">Choose a version to see changes.</p>
                            </div>
                        ) : historyData?.data?.voucher_no ? (
                            <div className="p-3 position-relative" style={{ minHeight: '400px' }}>
                                {dataLoading && (
                                    <div
                                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white"
                                        style={{ opacity: 0.7, zIndex: 10 }}
                                    >
                                        <Loader />
                                    </div>
                                )}
                                <div className="card border-0 mb-4" style={{ borderRadius: '10px' }}>
                                    <div className="card-body py-1 px-0">

                                        <div className="d-flex align-items-center justify-content-between flex-wrap">

                                            <div className="d-flex align-items-center">

                                                {/* Name + Email */}
                                                <div>
                                                    <div className="fw-semibold" style={{ fontSize: '16px', color: "var(--primary-clr)" }}>
                                                        {historyData?.data?.amendment_no === 0 ? "No Amendment data" : (historyData?.data?.amended_by?.user_name || "N/A")}
                                                    </div>
                                                    {historyData?.data?.amendment_no !== 0 && (
                                                        <div className="text-muted" style={{ fontSize: '12px' }}>
                                                            {historyData?.data?.amended_by?.email || "No email available"}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* 🔹 Right: Date */}
                                            <div className="text-md-end mt-2 mt-md-0">
                                                <div className="text-muted" style={{ fontSize: '11px' }}>Amended On</div>
                                                <div className="fw-semibold text-dark" style={{ fontSize: '13px' }}>
                                                    {historyData?.data?.amended_at
                                                        ? moment(historyData.data.amended_at).format("DD MMM YYYY, hh:mm A")
                                                        : "-"}
                                                </div>
                                            </div>

                                        </div>

                                        {/* 🔹 Divider */}
                                        <hr className="my-2" />

                                        {/* 🔹 Reason */}
                                        <div>
                                            <div className="text-muted mb-1" style={{ fontSize: '11px' }}>
                                                Reason for Amendment
                                            </div>
                                            <div className="text-dark" style={{ fontSize: '13px' }}>
                                                {historyData?.data?.amendment_no === 0 ? "No Amendment data" : (historyData?.data?.amendment_reason || "N/A.")}
                                            </div>
                                        </div>

                                    </div>
                                </div>

                                <h6 className="fw-bold mb-0 border-bottom pb-2" style={{ fontSize: '14px' }}>
                                    Changes Recorded
                                </h6>

                                {renderChangeLog(historyData?.data?.change_log)}
                            </div>
                        ) : (
                            <div className="p-3 position-relative d-flex align-items-center justify-content-center" style={{ minHeight: '400px' }}>
                                <div className="text-center py-4 text-muted small">No Amendment Data Found.</div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style jsx>{`
            .compact-history-dialog .list-group-item.active {
                background-color: var(--primary-clr) !important;
                border-color: var(--primary-clr) !important;
            }
            .compact-history-dialog .p-dialog-content {
                padding: 0 !important;
            }
            .compact-history-dialog .list-group-item {
                border-left: 4px solid transparent;
            }
            .compact-history-dialog .list-group-item.active {
                border-left: 4px solid #fff !important;
            }
            .bg-light {
                background-color: #f8f9fa !important;
            }
        `}</style>
        </Dialog>
    );
};

export default AmendHistoryModal;
